import Cookies from 'js-cookie'
import { useRecoilValue } from 'recoil'
import { useAws } from 'src/networking/aws'
import { useAwsGateError } from 'src/networking/aws/aws-gate-error.hook'
import { atomDossier } from 'src/storage'
import { AwsGateCallbacks, AwsGateRequest } from 'src/types'
import {
  GetPisIsAlive,
  GetPisPayment,
  GetPisProducts,
  PostPisPayment,
  PostPisPaymentCallback,
  PutPisUpdateState,
} from 'src/types/networking/terminals/pis.types'

export const useGetPisIsAlive = <
  T extends GetPisIsAlive['request'],
  D extends GetPisIsAlive['response']
>() => {
  const hook = useAws<T, D>('getPisAlive')
  const { saveGateError } = useAwsGateError('getPisAlive')

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
      }
      return hook.crossGate(
        {
          method: 'get',
          path: `/pis/isalive`,
          ...request,
        },
        {
          ...callbacks,
          onFailure: (gateError) => {
            saveGateError(gateError)
            return callbacks.onFailure
          },
        }
      )
    },
  }
}

export const useGetPisProducts = <
  T extends GetPisProducts['request'],
  D extends GetPisProducts['response']
>() => {
  const hook = useAws<T, D>('getPisProducts')

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
      }
      return hook.crossGate(
        {
          method: 'get',
          path: `/pis/products?iban=${request.uriParams.iban}`,
          ...request,
        },
        callbacks
      )
    },
  }
}

export const useGetPisPayment = <
  T extends GetPisPayment['request'],
  D extends GetPisPayment['response']
>() => {
  const hook = useAws<T, D>('getPisPayment')

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
      }
      return hook.crossGate(
        {
          method: 'get',
          path: `/pis/payments/${request.uriParams.idPayment}`,
          ...request,
        },
        callbacks
      )
    },
  }
}

export const usePostPisPayment = <
  T extends PostPisPayment['request'],
  D extends PostPisPayment['response']
>() => {
  const hook = useAws<T, D>('postPisPayments')
  const dossier = useRecoilValue(atomDossier)

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
        xDossierId: dossier?.dossierId || '',
        psuIpAddress: process.env.REACT_APP_CLIENT_ID || '',
        psuUserAgent: window.navigator.userAgent || '',
      }
      return hook.crossGate(
        {
          method: 'post',
          path: `/pis/payments/${request.uriParams.idPolizza}`,
          ...request,
        },
        callbacks
      )
    },
  }
}

export const usePostPisPaymentCallback = <
  T extends PostPisPaymentCallback['request'],
  D extends PostPisPaymentCallback['response']
>() => {
  const hook = useAws<T, D>('postPisPaymentCallback')
  const { saveGateError } = useAwsGateError('postPisPaymentCallback')

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
      }
      return hook.crossGate(
        {
          method: 'post',
          path: `/pis/payments/callback`,
          ...request,
        },
        {
          ...callbacks,
          onFailure: (gateError) => {
            saveGateError(gateError)
            return callbacks.onFailure
          },
        }
      )
    },
  }
}

export const usePutPisUpdateState = <
  T extends PutPisUpdateState['request'],
  D extends PutPisUpdateState['response']
>() => {
  const hook = useAws<T, D>('putPisUpdateState')

  return {
    ...hook,
    crossGate: (request: AwsGateRequest<T>, callbacks: AwsGateCallbacks<D>) => {
      request.headers = {
        ...request.headers,
        Authorization: Cookies.get('IdToken') || '',
      }
      return hook.crossGate(
        {
          method: 'put',
          path: `/pis/updateState/${request.uriParams.shopId}`,
          ...request,
        },
        callbacks
      )
    },
  }
}
